import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { Mail, MapPin, Phone, Send, Twitter, Linkedin, Instagram, Github } from 'lucide-react';
import { createMagneticEffect, fadeInOnScroll } from '../utils/animations';

const Contact = () => {
  const headerRef = useRef(null);
  const formRef = useRef(null);
  const magneticButtonRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
    budget: '',
  });

  useEffect(() => {
    // Header animation
    if (headerRef.current) {
      gsap.fromTo(
        headerRef.current.children,
        { opacity: 0, y: 100 },
        { opacity: 1, y: 0, stagger: 0.2, duration: 1, ease: 'power3.out' }
      );
    }

    // Form animation
    if (formRef.current) {
      const fields = formRef.current.querySelectorAll('.form-field');
      fadeInOnScroll(fields, { stagger: 0.1 });
    }

    // Magnetic effect on submit button
    let cleanup;
    if (magneticButtonRef.current) {
      cleanup = createMagneticEffect(magneticButtonRef.current, 0.4);
    }

    return () => {
      if (cleanup) cleanup();
    };
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock form submission
    console.log('Form submitted:', formData);

    // Success animation
    gsap.to(formRef.current, {
      scale: 0.95,
      duration: 0.2,
      yoyo: true,
      repeat: 1,
      ease: 'power2.inOut',
    });

    // Reset form
    setTimeout(() => {
      setFormData({
        name: '',
        email: '',
        company: '',
        message: '',
        budget: '',
      });
    }, 500);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="contact-page pt-32">
      {/* Header */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 mb-20">
        <div ref={headerRef} className="text-center">
          <span className="inline-block px-3 py-1 bg-[var(--surface-container-high)] text-[var(--on-surface-variant)] font-body text-xs tracking-code uppercase mb-6 rounded-sm">
            Get in Touch
          </span>
          <h1 className="font-headline text-5xl md:text-7xl lg:text-8xl font-bold tracking-headline uppercase mb-8 leading-[0.95]">
            Let's Build <br />
            <span className="text-[var(--primary)] text-neon-glow">
              Together
            </span>
          </h1>
          <p className="font-body text-[var(--on-surface-variant)] text-xl max-w-3xl mx-auto leading-relaxed">
            Have a project in mind? We'd love to hear about it.
            <br />
            Fill out the form below and we'll get back to you within 24 hours.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 mb-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Form */}
          <div>
            <form ref={formRef} onSubmit={handleSubmit} className="space-y-8">
              <div className="form-field">
                <label
                  htmlFor="name"
                  className="block font-body text-xs tracking-code uppercase text-[var(--on-surface-variant)] mb-3"
                >
                  Your Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full bg-transparent border-b-2 border-[var(--outline-variant)] focus:border-[var(--primary)] outline-none py-3 font-body text-[var(--on-surface)] transition-colors duration-300"
                  placeholder="John Doe"
                />
              </div>

              <div className="form-field">
                <label
                  htmlFor="email"
                  className="block font-body text-xs tracking-code uppercase text-[var(--on-surface-variant)] mb-3"
                >
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full bg-transparent border-b-2 border-[var(--outline-variant)] focus:border-[var(--primary)] outline-none py-3 font-body text-[var(--on-surface)] transition-colors duration-300"
                  placeholder="john@company.com"
                />
              </div>

              <div className="form-field">
                <label
                  htmlFor="company"
                  className="block font-body text-xs tracking-code uppercase text-[var(--on-surface-variant)] mb-3"
                >
                  Company Name
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full bg-transparent border-b-2 border-[var(--outline-variant)] focus:border-[var(--primary)] outline-none py-3 font-body text-[var(--on-surface)] transition-colors duration-300"
                  placeholder="Acme Inc."
                />
              </div>

              <div className="form-field">
                <label
                  htmlFor="budget"
                  className="block font-body text-xs tracking-code uppercase text-[var(--on-surface-variant)] mb-3"
                >
                  Project Budget
                </label>
                <select
                  id="budget"
                  name="budget"
                  value={formData.budget}
                  onChange={handleChange}
                  className="w-full bg-transparent border-b-2 border-[var(--outline-variant)] focus:border-[var(--primary)] outline-none py-3 font-body text-[var(--on-surface)] transition-colors duration-300"
                >
                  <option value="" className="bg-[var(--surface-container)]">
                    Select a range
                  </option>
                  <option
                    value="<50k"
                    className="bg-[var(--surface-container)]"
                  >
                    &lt; $50K
                  </option>
                  <option
                    value="50k-100k"
                    className="bg-[var(--surface-container)]"
                  >
                    $50K - $100K
                  </option>
                  <option
                    value="100k-250k"
                    className="bg-[var(--surface-container)]"
                  >
                    $100K - $250K
                  </option>
                  <option
                    value="250k+"
                    className="bg-[var(--surface-container)]"
                  >
                    $250K+
                  </option>
                </select>
              </div>

              <div className="form-field">
                <label
                  htmlFor="message"
                  className="block font-body text-xs tracking-code uppercase text-[var(--on-surface-variant)] mb-3"
                >
                  Project Details *
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  rows={6}
                  className="w-full bg-transparent border-b-2 border-[var(--outline-variant)] focus:border-[var(--primary)] outline-none py-3 font-body text-[var(--on-surface)] transition-colors duration-300 resize-none"
                  placeholder="Tell us about your project..."
                />
              </div>

              <button
                ref={magneticButtonRef}
                type="submit"
                className="group w-full bg-gradient-to-br from-[var(--primary)] to-[var(--primary-container)] text-[var(--on-primary-container)] px-10 py-5 font-headline font-bold text-sm uppercase tracking-code hover:brightness-110 transition-all duration-300 rounded-none flex items-center justify-center gap-3"
              >
                Send Message
                <Send
                  size={18}
                  className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
                />
              </button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="space-y-12">
            {/* Office Locations */}
            <div className="glass-panel p-10 border border-[var(--outline-variant)]/20">
              <h3 className="font-headline text-2xl font-bold uppercase mb-8">
                Office Locations
              </h3>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <MapPin
                    size={24}
                    className="text-[var(--primary)] flex-shrink-0"
                    strokeWidth={1.5}
                  />
                  <div>
                    <p className="font-headline text-lg font-bold mb-1">
                      INDIA
                    </p>
                    <p className="font-body text-[var(--on-surface-variant)] text-sm">
                      Noida sector 52
                    </p>
                  </div>
                </div>
                {/* <div className="flex gap-4">
                  <MapPin size={24} className="text-[var(--primary)] flex-shrink-0" strokeWidth={1.5} />
                  <div>
                    <p className="font-headline text-lg font-bold mb-1">London</p>
                    <p className="font-body text-[var(--on-surface-variant)] text-sm">
                      456 Innovation Lane
                      <br />
                      London, UK EC1A 1BB
                    </p>
                  </div>
                </div> */}
              </div>
            </div>

            {/* Direct Contact */}
            <div className="glass-panel p-10 border border-[var(--outline-variant)]/20">
              <h3 className="font-headline text-2xl font-bold uppercase mb-8">
                Direct Contact
              </h3>
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <Mail
                    size={24}
                    className="text-[var(--primary)] flex-shrink-0"
                    strokeWidth={1.5}
                  />
                  <a
                    href="mailto:hello@kineticether.com"
                    className="font-body text-[var(--on-surface)] hover:text-[var(--primary)] transition-colors"
                  >
                    info.kravion@gmail.com
                  </a>
                </div>
                <div className="flex items-center gap-4">
                  <Phone
                    size={24}
                    className="text-[var(--primary)] flex-shrink-0"
                    strokeWidth={1.5}
                  />
                  <a
                    href="tel:+91 8543810264"
                    className="font-body text-[var(--on-surface)] hover:text-[var(--primary)] transition-colors"
                  >
                    +91 85438-10264
                  </a>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="glass-panel p-10 border border-[var(--outline-variant)]/20">
              <h3 className="font-headline text-2xl font-bold uppercase mb-8">
                Follow Us.
              </h3>
              <div className="flex gap-4">
                {[
                  { icon: Twitter, href: "https://twitter.com" },
                  { icon: Linkedin, href: "https://linkedin.com" },
                  { icon: Instagram, href: "https://instagram.com" },
                  { icon: Github, href: "https://github.com" },
                ].map(({ icon: Icon, href }, index) => (
                  <a
                    key={index}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="magnetic w-12 h-12 bg-[var(--surface-container)] hover:bg-[var(--primary)] flex items-center justify-center transition-all duration-300 group"
                  >
                    <Icon
                      size={20}
                      className="text-[var(--on-surface)] group-hover:text-[var(--on-primary)] transition-colors"
                      strokeWidth={1.5}
                    />
                  </a>
                ))}
              </div>
            </div>

            {/* Response Time */}
            <div className="bg-[var(--surface-container-low)] p-8 border-l-4 border-[var(--primary)]">
              <p className="font-body text-[var(--on-surface-variant)] leading-relaxed">
                <span className="font-bold text-[var(--primary)]">
                  Quick Response:
                </span>{" "}
                We typically respond to all inquiries within 24 hours during
                business days.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
